import { Loading } from "../../app/Status";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import {
  dueText,
  formatEuro,
  nlsfbLabel,
  OBLIGATION_BASIS_APP_LABELS,
  OBLIGATION_KIND_LABELS,
  OBLIGATION_KINDS,
  OBLIGATION_STATUS_LABELS,
  obligationStatusLabel,
  todayIso,
  type ObligationKind,
  type ObligationListRow,
  type ObligationStatus,
} from "@klantpunt/shared";
import { IconInfo } from "../../app/icons";
import { api } from "../../lib/api";
import { useClientBuildingFilter } from "../../lib/clientContext";
import { AssetInfo } from "../mjop/planning/AssetInfo";
import { fmtDay, StatusChip } from "./ObligationSection";
import { STATUS_ORDER } from "./useObligations";

const monthLabel = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", { month: "long", year: "numeric" });

/** Gebouw (alleen in het organisatie-overzicht), verdieping › ruimte; leeg als niets bekend is. */
const whereText = (r: ObligationListRow, withBuilding: boolean) =>
  [withBuilding && r.building.code, r.asset && [r.asset.floorName, r.asset.roomName].filter(Boolean).join(" › ")]
    .filter(Boolean)
    .join(" · ");

/**
 * Onderhoud en keuringen op vervaldatum (docs/ASSETS-MJOP.md, "Weergave"): teller per status, filter op
 * status en soort, groepering per maand. Elke regel begint met de asset (code en naam), daaronder de
 * uitvoering en waar de asset staat. Voor één gebouw (onderdeel Onderhoud en keuringen) of de hele
 * organisatie (planner). Met `head` krijgt de lijst een eigen kop zoals de assetlijst (gebouwpagina).
 */
export function ObligationList({
  buildingId,
  onOpenAsset,
  assetFilter,
  head,
}: {
  /** Leeg = alle gebouwen van de organisatie. */
  buildingId?: string;
  onOpenAsset: (row: ObligationListRow) => void;
  /** Alleen keuringen van deze assets (plek boven de lijst: verdieping en ruimte). */
  assetFilter?: (assetId: string) => boolean;
  /** Kop boven de lijst: bovenschrift (gebouw en plek) en acties rechts. */
  head?: { label: string; actions?: ReactNode };
}) {
  const q = useQuery({
    queryKey: ["obligation-list", buildingId ?? "org"],
    queryFn: () => api<ObligationListRow[]>(buildingId ? `/buildings/${buildingId}/obligations` : "/obligations"),
  });
  /** ⓘ bij een regel: foto's, plek en gegevens van de asset (zoals in de MJOP-planning). */
  const [info, setInfo] = useState<ObligationListRow | null>(null);
  const [filter, setFilter] = useState<ObligationStatus | "attention" | "">("attention");
  const [kind, setKind] = useState<ObligationKind | "">("");
  const today = todayIso();
  // Organisatiebreed volgt de lijst de klantwisselaar.
  const ofClient = useClientBuildingFilter();
  const all = (q.data ?? []).filter(
    (r) =>
      r.status !== "na" &&
      (!assetFilter || (r.asset && assetFilter(r.asset.id))) &&
      (buildingId || ofClient(r.building.id)),
  );
  // Soorten die voorkomen, met aantal; de tellers per status volgen de gekozen soort.
  const kinds = OBLIGATION_KINDS.map((k) => [k, all.filter((r) => r.kind === k).length] as const).filter(([, n]) => n);
  const rows = kind ? all.filter((r) => r.kind === kind) : all;
  const counts = Object.fromEntries(STATUS_ORDER.map((s) => [s, rows.filter((r) => r.status === s).length])) as Record<
    ObligationStatus,
    number
  >;
  const shown = useMemo(
    () =>
      rows.filter((r) =>
        filter === "attention" ? r.status === "overdue" || r.status === "soon" : !filter || r.status === filter,
      ),
    [rows, filter],
  );
  // Per maand van de vervaldatum; zonder datum (Onbekend) achteraan.
  const groups = useMemo(() => {
    const m = new Map<string, ObligationListRow[]>();
    for (const r of shown) {
      const k = r.nextDueOn ? r.nextDueOn.slice(0, 7) : "zz";
      m.set(k, [...(m.get(k) ?? []), r]);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [shown]);

  return (
    <section className="oblist" aria-label="Onderhoud en keuringen" aria-busy={q.isPending}>
      {head && (
        <div className="tablehead">
          <div className="grow">
            <p className="label">{head.label}</p>
            <h1>
              Onderhoud en keuringen <span className="muted num">{all.length}</span>
            </h1>
          </div>
          {head.actions && <span className="pageactions">{head.actions}</span>}
        </div>
      )}
      <div className="obfilters">
        {kinds.length > 1 && (
          <select
            className="obkind"
            aria-label="Soort"
            value={kind}
            onChange={(e) => setKind(e.target.value as ObligationKind | "")}
          >
            <option value="">Alle soorten ({all.length})</option>
            {kinds.map(([k, n]) => (
              <option key={k} value={k}>
                {OBLIGATION_KIND_LABELS[k]} ({n})
              </option>
            ))}
          </select>
        )}
        <div className="obcounts" role="group" aria-label="Filter op status">
          <button
            type="button"
            className="kpi"
            aria-pressed={filter === "attention"}
            onClick={() => setFilter("attention")}
          >
            Verlopen en binnenkort <b className="num">{counts.overdue + counts.soon}</b>
          </button>
          {(["overdue", "soon", "unknown", "ok"] as const).map((s) => (
            <button
              key={s}
              type="button"
              className="kpi"
              aria-pressed={filter === s}
              onClick={() => setFilter(filter === s ? "" : s)}
            >
              <i className={`obdot ob-${s}`} aria-hidden="true" />
              {OBLIGATION_STATUS_LABELS[s]} <b className="num">{counts[s]}</b>
            </button>
          ))}
          <button type="button" className="kpi" aria-pressed={filter === ""} onClick={() => setFilter("")}>
            Alle <b className="num">{rows.length}</b>
          </button>
        </div>
      </div>
      {q.isPending ? (
        <Loading />
      ) : !all.length ? (
        <p className="muted">
          Nog geen onderhoud of keuringen. Open een asset{buildingId ? " of Bij het gebouw" : ""} en kies Uitvoering
          toevoegen.
        </p>
      ) : !shown.length ? (
        <p className="muted">Niets in deze selectie.</p>
      ) : (
        groups.map(([k, list]) => (
          <div key={k} className="obgroup">
            <h3 className="label">{k === "zz" ? "Zonder datum" : monthLabel(`${k}-01`)}</h3>
            <ul className="list compact">
              {list.map((r) => (
                <li key={r.id} className="obitem">
                  <button type="button" className="linkrow obrow" onClick={() => onOpenAsset(r)}>
                    <StatusChip
                      status={r.status}
                      small
                      label={obligationStatusLabel(r.status, r.kind, r.lastRecord?.result)}
                    />
                    <span className="grow">
                      <span className="block">
                        <span className="code small">{r.asset?.code ?? r.building.code}</span>{" "}
                        <strong>{r.asset?.name ?? "Bij het gebouw"}</strong>
                      </span>
                      <span className="small block obtask">
                        {r.name}
                        <span className="muted">
                          {` · ${OBLIGATION_KIND_LABELS[r.kind]} · ${OBLIGATION_BASIS_APP_LABELS[r.basis]}`}
                          {r.costCents != null && ` · ${formatEuro(r.costCents)}`}
                        </span>
                      </span>
                      {whereText(r, !buildingId) && (
                        <span className="muted small block">{whereText(r, !buildingId)}</span>
                      )}
                    </span>
                    <span className="small obwhen">
                      {fmtDay(r.nextDueOn)}
                      <span className="muted block">{r.nextDueOn ? dueText(r.nextDueOn, today) : ""}</span>
                    </span>
                  </button>
                  {r.asset && (
                    <button
                      type="button"
                      className="pl-ico pl-ainfo"
                      aria-label={`Informatie over ${r.asset.code} ${r.asset.name}`}
                      onClick={() => setInfo(r)}
                    >
                      <IconInfo size={16} aria-hidden="true" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
      {info?.asset && (
        <AssetInfo
          buildingId={info.building.id}
          a={info.asset}
          groupLabel={nlsfbLabel(info.asset.elementCode)}
          onClose={() => setInfo(null)}
        />
      )}
    </section>
  );
}
